import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, CheckCircle, ChevronRight, Globe, Shield, TrendingUp, Bot, Bell,
  Loader2, Sparkles, AlertTriangle, ExternalLink, Zap, Activity, Brain,
} from 'lucide-react';
import {
  getWizardStatus, saveWizardStep, completeWizard, skipWizard,
  getWizardRecommendations, getExchangeConnections,
} from '../services/api';

/**
 * 5-step onboarding wizard. Triggered automatically on first login (unless
 * already completed or skipped). Skippable from any step. State persists
 * server-side so users can resume from a "Resume Onboarding" card on the dashboard.
 *
 * Steps:
 *   1. Connect an exchange
 *   2. Pick risk profile (Conservative / Balanced / Aggressive)
 *   3. Choose a starter strategy (recommendations based on risk)
 *   4. Seed first bot (link to bots/new)
 *   5. Enable notifications
 */

const STEP_ICONS = {
  connect_exchange: Globe,
  risk_profile: Shield,
  choose_strategy: TrendingUp,
  create_first_bot: Bot,
  notifications: Bell,
};

export function useOnboardingTrigger() {
  const [shouldShow, setShouldShow] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getWizardStatus();
      const data = res?.data || res;
      setStatus(data);
      setShouldShow(!!data?.shouldShowWizard);
    } catch {
      // Silently fail — wizard is non-critical
      setShouldShow(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return { shouldShow, status, loading, refresh, dismiss: () => setShouldShow(false) };
}

export default function OnboardingWizard({ onClose, onComplete }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const [stepData, setStepData] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await getWizardStatus();
        const data = res?.data || res;
        if (!mounted) return;
        setStatus(data);

        // Pre-mark steps done based on what the user has already accomplished
        // OUTSIDE the wizard. Without this, the wizard sits at step 4 ("Seed
        // your first bot") forever even when the user already deployed a bot
        // via the regular Bots page. The backend tells us via `alreadyHasBot`.
        // Step indices: 0 connect_exchange, 1 risk_profile, 2 choose_strategy,
        // 3 create_first_bot, 4 notifications.
        let startStep = data.state?.currentStep || 0;
        if (data?.alreadyConnectedExchange) startStep = Math.max(startStep, 1);
        if (data?.alreadyHasBot) startStep = Math.max(startStep, 4);
        setCurrentStepIdx(Math.min(startStep, (data.steps?.length || 5) - 1));

        // Hydrate any prior selections
        if (data.state?.riskProfile) setStepData(p => ({ ...p, riskProfile: data.state.riskProfile }));
        if (data.state?.chosenStrategyId) setStepData(p => ({ ...p, strategyId: data.state.chosenStrategyId }));
        if (data.state?.notificationChannels) setStepData(p => ({ ...p, enabledChannels: data.state.notificationChannels }));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading || !status) {
    return (
      <Backdrop>
        <div className="bg-dark-800 rounded-2xl p-8 flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-accent-primary" />
          <span className="text-white text-sm">Loading wizard…</span>
        </div>
      </Backdrop>
    );
  }

  const steps = status.steps;
  const currentStep = steps[currentStepIdx];
  const isLastStep = currentStepIdx === steps.length - 1;

  async function handleAdvance(extraData = {}) {
    setBusy(true);
    setError(null);
    try {
      await saveWizardStep(currentStep.id, { ...stepData, ...extraData });
      if (isLastStep) {
        await completeWizard();
        if (onComplete) onComplete();
        if (onClose) onClose();
      } else {
        setCurrentStepIdx(i => i + 1);
      }
    } catch (err) {
      setError(err.message || 'Failed to save step');
    } finally {
      setBusy(false);
    }
  }

  async function handleSkip() {
    setBusy(true);
    try {
      await skipWizard();
      if (onClose) onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Backdrop>
      <div className="relative bg-[#0d0e14] border border-dark-600/50 rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-dark-700/50 bg-gradient-to-r from-accent-primary/5 via-violet-500/5 to-cyan-500/5 rounded-t-2xl">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-accent-primary/15 border border-accent-primary/30 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-accent-primary" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest font-semibold text-accent-primary mb-0.5">Welcome to haythix</p>
                <h2 className="text-xl font-bold text-white">Let's set up your account</h2>
              </div>
            </div>
            {/*
              X = same behavior as the "Skip — I'll do this later" button at the bottom.
              We MUST persist the dismissal via skipWizard() — without that, the parent's
              `useOnboardingTrigger` re-fetches status, the backend still says
              "shouldShowWizard: true" (user has no exchange yet), and the wizard pops back
              within milliseconds. The X-as-fake-close was a launch-blocking UX bug.
            */}
            <button
              onClick={handleSkip}
              disabled={busy}
              title="Close (skip onboarding for now)"
              className="p-1.5 rounded-lg hover:bg-white/5 text-gray-400 disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Progress bar */}
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 overflow-x-auto">
              {steps.map((s, i) => {
                const StepIcon = STEP_ICONS[s.id] || CheckCircle;
                const done = i < currentStepIdx;
                const active = i === currentStepIdx;
                return (
                  <div key={s.id} className="flex items-center gap-1.5 flex-shrink-0">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                      done ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' :
                      active ? 'bg-accent-primary/20 text-accent-primary border border-accent-primary/40' :
                      'bg-dark-700 text-gray-600 border border-dark-600'
                    }`}>
                      {done ? <CheckCircle className="w-4 h-4" /> : <StepIcon className="w-3.5 h-3.5" />}
                    </div>
                    {i < steps.length - 1 && <div className={`w-6 h-0.5 ${i < currentStepIdx ? 'bg-emerald-500/40' : 'bg-dark-600'}`} />}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-500">
              Step {currentStepIdx + 1} of {steps.length} · {currentStep.title}
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3 text-red-400 text-xs mb-4">{error}</div>
          )}

          {currentStep.id === 'connect_exchange' && (
            <ConnectExchangeStep
              navigate={navigate}
              alreadyConnected={status.alreadyConnectedExchange}
              onAdvance={() => handleAdvance()}
              onClose={onClose}
              busy={busy}
            />
          )}
          {currentStep.id === 'risk_profile' && (
            <RiskProfileStep
              profiles={status.riskProfiles}
              selected={stepData.riskProfile}
              onSelect={p => setStepData(d => ({ ...d, riskProfile: p }))}
              onAdvance={() => stepData.riskProfile && handleAdvance({ profile: stepData.riskProfile })}
              busy={busy}
            />
          )}
          {currentStep.id === 'choose_strategy' && (
            <ChooseStrategyStep
              profile={stepData.riskProfile}
              selected={stepData.strategyId}
              onSelect={s => setStepData(d => ({ ...d, strategyId: s }))}
              onAdvance={() => handleAdvance({ strategyId: stepData.strategyId || 'skip' })}
              busy={busy}
            />
          )}
          {currentStep.id === 'create_first_bot' && (
            <CreateFirstBotStep
              navigate={navigate}
              chosenStrategyId={stepData.strategyId}
              onAdvance={() => handleAdvance({ botId: 'pending' })}
              onClose={onClose}
              busy={busy}
            />
          )}
          {currentStep.id === 'notifications' && (
            <NotificationsStep
              channels={status.notificationChannels}
              selected={stepData.enabledChannels || status.notificationChannels.filter(c => c.defaultOn).map(c => c.id)}
              onChange={list => setStepData(d => ({ ...d, enabledChannels: list }))}
              onAdvance={() => handleAdvance({ enabledChannels: stepData.enabledChannels || status.notificationChannels.filter(c => c.defaultOn).map(c => c.id) })}
              busy={busy}
            />
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-dark-700/50 flex items-center justify-between">
          <button onClick={handleSkip} disabled={busy} className="text-xs text-gray-500 hover:text-white">
            Skip — I'll do this later
          </button>
          {currentStepIdx > 0 && (
            <button onClick={() => setCurrentStepIdx(i => Math.max(0, i - 1))} disabled={busy} className="text-xs text-gray-400 hover:text-white">
              ← Back
            </button>
          )}
        </div>
      </div>
    </Backdrop>
  );
}

function Backdrop({ children }) {
  return (
    <div className="fixed inset-0 z-[9998] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      {children}
    </div>
  );
}

// ─── Step 1: Connect Exchange ────────────────────────────────────────────────

function ConnectExchangeStep({ navigate, alreadyConnected, onAdvance, onClose, busy }) {
  const [refreshing, setRefreshing] = useState(false);
  const [hasNow, setHasNow] = useState(alreadyConnected);

  async function check() {
    setRefreshing(true);
    try {
      const res = await getExchangeConnections();
      const list = res?.exchanges?.filter(e => e.connected) || [];
      setHasNow(list.length > 0);
    } catch {} finally {
      setRefreshing(false);
    }
  }

  return (
    <div>
      <div className="flex items-start gap-3 mb-4">
        <Globe className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-base font-bold text-white mb-1">Connect an exchange</h3>
          <p className="text-sm text-gray-400">
            Link your CEX so haythix can place orders for your bots. We use <span className="text-emerald-400">read + spot trading</span> permissions only — never withdrawals.
          </p>
        </div>
      </div>

      {hasNow ? (
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4 flex items-start gap-3 mb-4">
          <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-emerald-400 text-sm font-bold">Exchange connected</p>
            <p className="text-xs text-gray-400 mt-0.5">Nice. Click Continue to pick your risk profile.</p>
          </div>
        </div>
      ) : (
        <div className="bg-dark-700/30 rounded-lg p-4 mb-4">
          <p className="text-xs text-gray-400 mb-3">Recommended starter exchanges (Starter+ tier):</p>
          <div className="space-y-2">
            {['Binance', 'Coinbase', 'Kraken'].map(name => (
              <div key={name} className="flex items-center gap-2 text-sm text-gray-300">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> {name}
              </div>
            ))}
          </div>
          <button
            onClick={() => { onClose(); navigate('/settings?tab=connections'); }}
            className="w-full mt-4 bg-accent-primary text-black font-semibold py-2.5 rounded-xl hover:bg-accent-primary/90 transition-colors flex items-center justify-center gap-2"
          >
            Open Settings → Connections <ExternalLink className="w-4 h-4" />
          </button>
          <button onClick={check} disabled={refreshing} className="w-full mt-2 text-xs text-gray-500 hover:text-white py-1">
            {refreshing ? 'Checking…' : "I've connected — refresh"}
          </button>
        </div>
      )}

      <button onClick={onAdvance} disabled={!hasNow || busy}
        className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:bg-dark-600 disabled:text-gray-500 text-black font-semibold py-3 rounded-xl flex items-center justify-center gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ChevronRight className="w-4 h-4" /></>}
      </button>
    </div>
  );
}

// ─── Step 2: Risk Profile ────────────────────────────────────────────────────

function RiskProfileStep({ profiles, selected, onSelect, onAdvance, busy }) {
  return (
    <div>
      <div className="flex items-start gap-3 mb-4">
        <Shield className="w-6 h-6 text-violet-400 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-base font-bold text-white mb-1">Pick your risk profile</h3>
          <p className="text-sm text-gray-400">We use this to recommend strategies. You can change it any time.</p>
        </div>
      </div>

      <div className="space-y-2">
        {profiles.map(p => {
          const active = selected === p.id;
          return (
            <button key={p.id} onClick={() => onSelect(p.id)}
              className={`w-full text-left p-4 rounded-xl border transition-all ${active ? 'bg-violet-500/10 border-violet-400/40' : 'bg-dark-700/30 border-dark-600/40 hover:border-dark-500'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-bold text-white">{p.label}</span>
                {active && <CheckCircle className="w-4 h-4 text-violet-400" />}
              </div>
              <p className="text-xs text-gray-400 mb-2">{p.description}</p>
              <div className="grid grid-cols-3 gap-2 text-[10px]">
                <Stat label="Drawdown" value={p.typicalDrawdown} />
                <Stat label="Annual" value={p.typicalAnnualReturn} />
                <Stat label="Leverage" value={p.leverageGuidance} />
              </div>
              <p className="text-[10px] text-gray-500 mt-2">{p.recommendedAllocation}</p>
            </button>
          );
        })}
      </div>

      <button onClick={onAdvance} disabled={!selected || busy}
        className="w-full mt-4 bg-violet-500 hover:bg-violet-400 disabled:bg-dark-600 disabled:text-gray-500 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ChevronRight className="w-4 h-4" /></>}
      </button>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-dark-800/50 rounded p-1.5">
      <p className="text-gray-500">{label}</p>
      <p className="text-white font-mono">{value}</p>
    </div>
  );
}

// ─── Step 3: Choose Strategy ────────────────────────────────────────────────

function ChooseStrategyStep({ profile, selected, onSelect, onAdvance, busy }) {
  const [recs, setRecs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getWizardRecommendations(profile)
      .then(res => setRecs(res?.data?.profile || res?.profile))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [profile]);

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-accent-primary" /></div>;
  if (!recs) return <p className="text-red-400 text-sm">Couldn't load recommendations.</p>;

  return (
    <div>
      <div className="flex items-start gap-3 mb-4">
        <TrendingUp className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-base font-bold text-white mb-1">Recommended for {recs.label}</h3>
          <p className="text-sm text-gray-400">Pick a starter strategy. You can always swap or add more later.</p>
        </div>
      </div>

      <div className="space-y-2">
        {recs.recommendedStrategies.map(s => {
          const active = selected === s.id;
          return (
            <button key={s.id} onClick={() => onSelect(s.id)}
              className={`w-full text-left p-3 rounded-xl border transition-all ${active ? 'bg-emerald-500/10 border-emerald-400/40' : 'bg-dark-700/30 border-dark-600/40 hover:border-dark-500'}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-white">{s.name}</span>
                {active && <CheckCircle className="w-4 h-4 text-emerald-400" />}
              </div>
              <p className="text-xs text-gray-400 mt-0.5">{s.reason}</p>
            </button>
          );
        })}
      </div>

      <button onClick={onAdvance} disabled={busy}
        className="w-full mt-4 bg-emerald-500 hover:bg-emerald-400 disabled:bg-dark-600 disabled:text-gray-500 text-black font-semibold py-3 rounded-xl flex items-center justify-center gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{selected ? 'Continue' : 'Skip — I\'ll choose later'} <ChevronRight className="w-4 h-4" /></>}
      </button>
    </div>
  );
}

// ─── Step 4: Create First Bot ────────────────────────────────────────────────

function CreateFirstBotStep({ navigate, chosenStrategyId, onAdvance, onClose, busy }) {
  return (
    <div>
      <div className="flex items-start gap-3 mb-4">
        <Bot className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-base font-bold text-white mb-1">Seed your first bot</h3>
          <p className="text-sm text-gray-400">
            Quick start: open the bot creator pre-filled with your chosen strategy. Or skip to set up bots later.
          </p>
        </div>
      </div>

      <div className="bg-dark-700/30 rounded-lg p-4 mb-4 space-y-2">
        <p className="text-xs text-gray-400">Three quick paths:</p>
        <button onClick={() => { onClose(); navigate('/grid-bot'); }}
          className="w-full text-left p-3 rounded-lg bg-dark-700/50 border border-dark-600/40 hover:border-emerald-400/40">
          <div className="flex items-center gap-2 text-sm font-bold text-white"><Sparkles className="w-4 h-4 text-emerald-400" /> Grid Bot</div>
          <p className="text-xs text-gray-500 mt-0.5">Set & forget — earns from oscillation in a price range.</p>
        </button>
        <button onClick={() => { onClose(); navigate('/smart-trade'); }}
          className="w-full text-left p-3 rounded-lg bg-dark-700/50 border border-dark-600/40 hover:border-cyan-400/40">
          <div className="flex items-center gap-2 text-sm font-bold text-white"><Zap className="w-4 h-4 text-cyan-400" /> Smart Trade Terminal</div>
          <p className="text-xs text-gray-500 mt-0.5">Manual entry with multi-TP ladder + trailing SL.</p>
        </button>
        <button onClick={() => { onClose(); navigate('/bots/new'); }}
          className="w-full text-left p-3 rounded-lg bg-dark-700/50 border border-dark-600/40 hover:border-violet-400/40">
          <div className="flex items-center gap-2 text-sm font-bold text-white"><Brain className="w-4 h-4 text-violet-400" /> Custom Bot</div>
          <p className="text-xs text-gray-500 mt-0.5">Build your own from indicators or NL prompt.</p>
        </button>
      </div>

      <button onClick={onAdvance} disabled={busy}
        className="w-full bg-cyan-500 hover:bg-cyan-400 disabled:bg-dark-600 disabled:text-gray-500 text-black font-semibold py-3 rounded-xl flex items-center justify-center gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ChevronRight className="w-4 h-4" /></>}
      </button>
    </div>
  );
}

// ─── Step 5: Notifications ───────────────────────────────────────────────────

function NotificationsStep({ channels, selected, onChange, onAdvance, busy }) {
  function toggle(id) {
    if (selected.includes(id)) onChange(selected.filter(c => c !== id));
    else onChange([...selected, id]);
  }
  return (
    <div>
      <div className="flex items-start gap-3 mb-4">
        <Bell className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-base font-bold text-white mb-1">Enable notifications</h3>
          <p className="text-sm text-gray-400">Get alerts when your bots fill, hit TP, or trigger SL.</p>
        </div>
      </div>

      <div className="space-y-2">
        {channels.map(c => {
          const on = selected.includes(c.id);
          return (
            <label key={c.id} className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer ${on ? 'bg-amber-500/5 border-amber-400/30' : 'bg-dark-700/30 border-dark-600/40 hover:border-dark-500'}`}>
              <div>
                <p className="text-sm font-medium text-white">{c.label}</p>
                {c.requiresLink && <p className="text-[10px] text-gray-500 mt-0.5">You'll need to link your account in Settings → Notifications.</p>}
              </div>
              <input type="checkbox" checked={on} onChange={() => toggle(c.id)} className="rounded" />
            </label>
          );
        })}
      </div>

      <button onClick={onAdvance} disabled={busy}
        className="w-full mt-4 bg-amber-500 hover:bg-amber-400 disabled:bg-dark-600 disabled:text-gray-500 text-black font-semibold py-3 rounded-xl flex items-center justify-center gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4" /> Finish onboarding</>}
      </button>
    </div>
  );
}
